/*
 * Keksmeister cookie banner (sections/km-consent.liquid), 23.10.2026.
 *
 * Source of truth is Shopify's Customer Privacy API (cookie _tracking_consent), as documented on
 * https://shopify.dev/docs/api/customer-privacy: the API is loaded with loadFeatures('consent-tracking-api', 0.2),
 * a decision is only recorded on a click (setTrackingConsent), Shopify's web pixels (Google & YouTube, custom
 * pixels) follow it through the visitorConsentCollected event. No localStorage of our own.
 *
 * - Banner for every visitor without a decision (all regions), and again after 12 months: the decision date is
 *   stored with the consent itself as consent metafield "km_cb" = "<version>|<YYYY-MM-DD>".
 * - Our own features ask window.kmConsent.allowed(purpose) and need an explicit "yes" (stricter than the
 *   ...Allowed() methods, which are true before a decision in regions without a consent requirement).
 * - <script type="text/plain" data-km-consent="marketing" data-src="…"> runs only after that consent.
 * - Withdrawing analytics/marketing removes the cookies those tools left on keksmeister.de and reloads the page,
 *   so scripts that are already running stop. Shopify's own cookies are never touched (Shopify's rule).
 * - The footer button [data-km-consent-open] and links to #cookie-einstellungen reopen the settings.
 */
(() => {
  if (window.kmConsent) return;

  const PURPOSES = ['analytics', 'marketing', 'preferences'];
  const STAMP_KEY = 'km_cb';
  const MAX_AGE_DAYS = 365;
  const root = document.getElementById('km-consent');
  const version = (root && root.dataset.version) || '1';

  /** @type {any} */
  let api = null;
  /** @type {Element | null} */
  let opener = null;

  // ---------------------------------------------------------------- Customer Privacy API
  const ready = new Promise((resolve) => {
    let settled = false;
    const done = (/** @type {any} */ value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    window.setTimeout(() => done(null), 10000);
    let tries = 0;
    const load = () => {
      const shopify = /** @type {any} */ (window).Shopify;
      if (shopify && shopify.customerPrivacy && shopify.customerPrivacy.setTrackingConsent) return done(shopify.customerPrivacy);
      if (shopify && typeof shopify.loadFeatures === 'function') {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (/** @type {any} */ error) => {
          const cp = /** @type {any} */ (window).Shopify && /** @type {any} */ (window).Shopify.customerPrivacy;
          if (error || !cp) {
            console.warn('[km-consent] Customer Privacy API not available', error);
            return done(null);
          }
          done(cp);
        });
        return;
      }
      if (++tries > 80) return done(null);
      window.setTimeout(load, 100);
    };
    load();
  }).then((cp) => {
    api = cp;
    return cp;
  });

  /** @returns {Record<string, string>} */
  const current = () => {
    try {
      return (api && api.currentVisitorConsent && api.currentVisitorConsent()) || {};
    } catch {
      return {};
    }
  };

  const yes = (/** @type {string} */ purpose) => current()[purpose] === 'yes';
  const decided = () => PURPOSES.every((p) => current()[p] === 'yes' || current()[p] === 'no');

  const today = () => new Date().toISOString().slice(0, 10);

  /** Decision too old (> 12 months) or made for an older banner version. Unknown stamp = not expired. */
  const expired = () => {
    try {
      const raw = api && api.getTrackingConsentMetafield ? api.getTrackingConsentMetafield(STAMP_KEY) : null;
      const value = typeof raw === 'string' ? raw : raw && typeof raw.value === 'string' ? raw.value : '';
      if (!value) return false;
      const [v, date] = value.split('|');
      if (v !== version) return true;
      const age = (Date.now() - Date.parse(date + 'T00:00:00Z')) / 86400000;
      return !(age >= 0 && age <= MAX_AGE_DAYS);
    } catch {
      return false;
    }
  };

  // ---------------------------------------------------------------- consent-gated scripts and cleanup
  const activateScripts = () => {
    document.querySelectorAll('script[type="text/plain"][data-km-consent]').forEach((placeholder) => {
      const need = (placeholder.getAttribute('data-km-consent') || '').split(/[\s,]+/).filter(Boolean);
      if (!need.length || !need.every(yes)) return;
      const script = document.createElement('script');
      for (const attr of Array.from(placeholder.attributes)) {
        if (attr.name === 'type' || attr.name === 'data-src' || attr.name === 'data-km-consent') continue;
        script.setAttribute(attr.name, attr.value);
      }
      const src = placeholder.getAttribute('data-src');
      if (src) {
        script.src = src;
        script.async = true;
      } else {
        script.textContent = placeholder.textContent;
      }
      placeholder.replaceWith(script);
    });
  };

  /** Cookies and storage the tools leave on our domain (see Datenschutzerklärung, Abschnitt 5). */
  const CLEANUP = {
    analytics: { cookies: [/^_ga(_.*)?$/, /^_gid$/], storage: [] },
    marketing: { cookies: [/^_gcl_/, /^_fbp$/, /^_fbc$/, /^_ma_/], storage: [/^_ma_/] },
    preferences: { cookies: [], storage: [/^km-viewed$/, /^viewedProducts$/] },
  };

  const clean = (/** @type {'analytics' | 'marketing' | 'preferences'} */ purpose) => {
    const rule = CLEANUP[purpose];
    const host = location.hostname;
    const domains = ['', host, '.' + host, '.' + host.replace(/^www\./, '')];
    for (const pair of document.cookie.split(';')) {
      const name = pair.split('=')[0].trim();
      if (!name || !rule.cookies.some((rx) => rx.test(name))) continue;
      for (const domain of domains) {
        document.cookie = `${name}=; Max-Age=0; path=/${domain ? '; domain=' + domain : ''}`;
      }
    }
    for (const store of [window.localStorage, window.sessionStorage]) {
      try {
        for (const key of Object.keys(store)) if (rule.storage.some((rx) => rx.test(key))) store.removeItem(key);
      } catch {
        /* storage blocked */
      }
    }
  };

  // ---------------------------------------------------------------- banner UI
  const switches = () =>
    /** @type {Record<string, HTMLInputElement | null>} */ (
      Object.fromEntries(PURPOSES.map((p) => [p, root ? root.querySelector(`input[name="${p}"]`) : null]))
    );

  const status = (/** @type {string} */ text) => {
    const el = root && root.querySelector('[data-km-consent-status]');
    if (el) el.textContent = text;
  };

  const show = (/** @type {'intro' | 'settings'} */ view, /** @type {boolean} */ focus) => {
    if (!root) return;
    const c = current();
    // Second layer shows the stored choice; nothing is pre-ticked for someone who has not decided yet.
    for (const [p, input] of Object.entries(switches())) if (input) input.checked = c[p] === 'yes';
    root.dataset.view = view;
    root.hidden = false;
    status('');
    if (focus) root.querySelector('.km-consent__title')?.focus({ preventScroll: true });
  };

  const hide = () => {
    if (!root) return;
    root.hidden = true;
    root.dataset.view = 'intro';
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
    opener = null;
  };

  /**
   * Records a decision in Shopify (only ever called from a click).
   * @param {{analytics: boolean, marketing: boolean, preferences: boolean}} choice
   */
  const save = async (choice, /** @type {HTMLElement | null} */ button) => {
    const cp = await ready;
    if (!cp) {
      status('Speichern hat nicht geklappt. Lade die Seite bitte neu und versuch es noch einmal.');
      return;
    }
    const before = current();
    button?.setAttribute('aria-busy', 'true');
    const result = await new Promise((resolve) => {
      try {
        cp.setTrackingConsent(
          {
            analytics: choice.analytics,
            marketing: choice.marketing,
            preferences: choice.preferences,
            sale_of_data: choice.marketing,
            metafields: [{ key: STAMP_KEY, value: `${version}|${today()}` }],
          },
          (/** @type {any} */ response) => resolve(response && response.error ? { error: response.error } : { ok: true })
        );
      } catch (error) {
        resolve({ error });
      }
    });
    button?.removeAttribute('aria-busy');
    if (/** @type {any} */ (result).error) {
      console.warn('[km-consent] setTrackingConsent failed', /** @type {any} */ (result).error);
      status('Speichern hat nicht geklappt. Lade die Seite bitte neu und versuch es noch einmal.');
      return;
    }
    hide();
    let reload = false;
    for (const p of /** @type {const} */ (['analytics', 'marketing', 'preferences'])) {
      if (!choice[p]) {
        clean(p);
        if (before[p] === 'yes' && p !== 'preferences') reload = true;
      }
    }
    activateScripts();
    document.dispatchEvent(new CustomEvent('km:consent', { detail: { ...choice } }));
    if (reload) window.setTimeout(() => window.location.reload(), 150);
  };

  if (root) {
    root.addEventListener('click', (event) => {
      const button = /** @type {Element} */ (event.target).closest('[data-km-consent-action]');
      if (!(button instanceof HTMLElement)) return;
      const action = button.dataset.kmConsentAction;
      if (action === 'accept') save({ analytics: true, marketing: true, preferences: true }, button);
      else if (action === 'necessary') save({ analytics: false, marketing: false, preferences: false }, button);
      else if (action === 'settings') show('settings', true);
      else if (action === 'save') {
        const s = switches();
        save({ analytics: !!s.analytics?.checked, marketing: !!s.marketing?.checked, preferences: !!s.preferences?.checked }, button);
      }
    });
    // Escape closes only a reopened banner (a first-time visitor still has to choose).
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && decided() && !expired()) hide();
    });
  }

  const open = (/** @type {Element | null} */ from) => {
    opener = from || null;
    ready.then(() => show('settings', true));
  };

  document.addEventListener('click', (event) => {
    const trigger = /** @type {Element} */ (event.target).closest('[data-km-consent-open], a[href$="#cookie-einstellungen"]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  document.addEventListener('visitorConsentCollected', () => activateScripts());

  // ---------------------------------------------------------------- public helper for our own features
  window.kmConsent = {
    ready,
    /** Explicit consent for a purpose ('analytics' | 'marketing' | 'preferences'). */
    allowed: (/** @type {string} */ purpose) => ready.then((cp) => !!cp && yes(purpose)),
    open: () => open(null),
  };
  document.dispatchEvent(new CustomEvent('km:consent-api'));

  ready.then((cp) => {
    if (!cp) return;
    activateScripts();
    if (location.hash === '#cookie-einstellungen') return open(null);
    if (!decided() || expired()) show('intro', false);
  });
})();
